import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { removeSpaces } from "@ryde/shared";

import { ISystemParamsTabViewModel } from "../use-system-params-edit";
import {
  SystemParamsCategoryChipStyled,
  SystemParamsCategorySwitchStyled,
} from "./system-params.category-switch.styled";
import { SYSTEM_PARAM_CATEGORY_PALETTE } from "./system-params.category.constants";
import { getSystemParamCategoryHeaderColors } from "./system-params.category.utils";

interface ISystemParamsCategorySwitchProps {
  tabViewModels: ISystemParamsTabViewModel[];
  tabIndex: number;
  onTabSelect: (originalIndex: number) => void;
  isSearching: boolean;
  isInnerItemEditing: boolean;
}

export const SystemParamsCategorySwitch: FC<
  ISystemParamsCategorySwitchProps
> = ({
  tabViewModels,
  tabIndex,
  onTabSelect,
  isSearching,
  isInnerItemEditing,
}) => {
  const { t } = useTranslation();

  const options = useMemo(() => {
    return tabViewModels.map((vm, index) => {
      const { tab, originalIndex } = vm;
      const tabName = t(
        `selectListLabel.${removeSpaces(tab.label?.toLowerCase())}`,
      );
      const countSuffix = isSearching ? ` (${vm.count})` : "";

      return {
        originalIndex,
        label: `${tabName || `${t("general.TAB")}-${index + 1}`}${countSuffix}`,
        categoryLabel: tab.label,
        e2eId: `${tab.label}_${originalIndex}`,
      };
    });
  }, [isSearching, t, tabViewModels]);

  const handleSelect = useEvent((originalIndex: number): void => {
    onTabSelect(originalIndex);
  });

  return (
    <SystemParamsCategorySwitchStyled
      className={clsx(isInnerItemEditing && "inner-item-editing")}
      role="radiogroup"
      aria-label={t("general.TAB")}
    >
      {options.map(option => {
        const isSelected = option.originalIndex === tabIndex;
        const colors = isSelected
          ? getSystemParamCategoryHeaderColors(option.categoryLabel)
          : SYSTEM_PARAM_CATEGORY_PALETTE.idle;

        return (
          <SystemParamsCategoryChipStyled
            key={option.e2eId}
            data-e2e-id={option.e2eId}
            role="radio"
            aria-checked={isSelected}
            $isSelected={isSelected}
            $colors={colors}
            onClick={() => handleSelect(option.originalIndex)}
          >
            {option.label}
          </SystemParamsCategoryChipStyled>
        );
      })}
    </SystemParamsCategorySwitchStyled>
  );
};
